<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  lastBindingsFromExecutionTrace,
  writePinnedExecution,
} from './workflowEditorPinnedExecution';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const workflowId = computed(() => route.params.workflowId);
const executionId = computed(() => route.params.executionId);

const isLoading = ref(true);
const payload = ref({});
const graph = ref({ nodes: [], edges: [] });
const selectedIndex = ref(0);

const trace = computed(() =>
  Array.isArray(payload.value.trace) ? payload.value.trace : []
);

const nodesById = computed(() => {
  const nodes = graph.value?.nodes || [];
  const map = {};
  nodes.forEach(n => {
    if (n?.id != null) map[String(n.id)] = n;
  });
  return map;
});

const selectedStep = computed(() => trace.value[selectedIndex.value] || null);

const showTechnical = ref(false);

const humanizeKey = key =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

/** Mesma regra que `ConversationWorkflows::WorkflowVariableInterpolator` no servidor. */
const valueAtBindingPath = (bindings, path) => {
  if (!bindings || typeof bindings !== 'object' || path == null || path === '') {
    return undefined;
  }
  const keys = String(path).split('.');
  let cur = bindings;
  for (const key of keys) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
};

const interpolateWorkflowTemplate = (template, bindings) => {
  if (typeof template !== 'string' || !template) return template;
  if (!bindings || typeof bindings !== 'object') return template;
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  return template.replace(re, (_, path) => {
    const v = valueAtBindingPath(bindings, path);
    if (v === undefined || v === null) return '';
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  });
};

const formatScalar = v => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? `${v.length} itens` : '—';
  if (typeof v === 'object') {
    try {
      const s = JSON.stringify(v);
      return s.length > 280 ? `${s.slice(0, 280)}…` : s;
    } catch {
      return String(v);
    }
  }
  return String(v);
};

const friendlyBindingRows = bindings => {
  const rows = [];
  if (!bindings || typeof bindings !== 'object') return rows;

  const conv = bindings.conversation;
  if (conv && typeof conv === 'object') {
    if (conv.display_id != null) {
      rows.push({
        label: 'Conversa',
        value: `Nº ${conv.display_id}`,
      });
    }
    if (conv.status != null && conv.status !== '') {
      rows.push({ label: 'Estado da conversa', value: String(conv.status) });
    }
    if (conv.priority != null && conv.priority !== '') {
      rows.push({ label: 'Prioridade', value: String(conv.priority) });
    }
  }

  const contact = bindings.contact;
  if (contact && typeof contact === 'object') {
    const name = contact.name || '';
    const phone = contact.phone_number || '';
    const email = contact.email || '';
    const bits = [name, phone || email].filter(Boolean);
    if (bits.length) {
      rows.push({ label: 'Cliente / contato', value: bits.join(' · ') });
    }
  }

  const msg = bindings.message;
  if (msg && typeof msg === 'object' && Object.keys(msg).length) {
    if (msg.content != null && String(msg.content).trim() !== '') {
      const c = String(msg.content);
      rows.push({
        label: 'Mensagem que disparou o passo',
        value: c.length > 220 ? `${c.slice(0, 220)}…` : c,
      });
    }
    if (msg.message_type) {
      rows.push({ label: 'Tipo de mensagem', value: String(msg.message_type) });
    }
  }

  const up = bindings.upstream?.http_response || bindings.upstream?.http;
  if (up && typeof up === 'object') {
    const code = up.status_code ?? up.statusCode;
    if (code != null && code !== '') {
      rows.push({ label: 'Resposta HTTP anterior (código)', value: String(code) });
    }
    const body = up.body?.toString?.() || '';
    if (body.length) {
      rows.push({
        label: 'Resposta HTTP anterior (trecho)',
        value: body.length > 180 ? `${body.slice(0, 180)}…` : body,
      });
    }
  }

  const kb = bindings.kanban_item;
  if (kb && typeof kb === 'object' && Object.keys(kb).length) {
    const title = kb.title || kb.item_details?.title || '';
    const stage = kb.funnel_stage || '';
    const parts = [title, stage && `Etapa: ${stage}`].filter(Boolean);
    if (parts.length) {
      rows.push({ label: 'Cartão no Kanban', value: parts.join(' · ') });
    }
  }

  return rows;
};

const friendlyConfigRows = (config, { kind, bindings } = {}) => {
  if (!config || typeof config !== 'object') return [];
  const skip = new Set(['id', 'kind']);
  const interpolateContentKeys =
    kind === 'send_message'
      ? new Set(['message_content'])
      : kind === 'add_private_note'
        ? new Set(['private_note_content'])
        : new Set();

  return Object.entries(config)
    .filter(([k]) => !skip.has(k))
    .map(([k, v]) => {
      if (
        interpolateContentKeys.has(k) &&
        typeof v === 'string' &&
        bindings &&
        typeof bindings === 'object'
      ) {
        return {
          label: humanizeKey(k),
          value: interpolateWorkflowTemplate(v, bindings),
        };
      }
      return {
        label: humanizeKey(k),
        value: formatScalar(v),
      };
    });
};

const friendlyOutputRows = output => {
  const rows = [];
  if (!output || typeof output !== 'object') return rows;

  if (output.branch_handle != null && output.branch_handle !== '') {
    rows.push({
      label: 'Caminho da condição',
      value: String(output.branch_handle),
    });
  }
  if (output.advanced === true || output.advanced === 'true') {
    rows.push({ label: 'Gatilho', value: 'Acionado; o fluxo seguiu adiante.' });
  }
  if (output.scheduled === true || output.scheduled === 'true') {
    rows.push({
      label: 'Espera agendada',
      value: 'Sim — o fluxo continua após o tempo definido.',
    });
  }
  if (output.wait_minutes != null) {
    rows.push({
      label: 'Tempo de espera (minutos)',
      value: String(output.wait_minutes),
    });
  }
  if (output.next_node_id != null && output.next_node_id !== '') {
    rows.push({
      label: 'Próximo passo (referência interna)',
      value: String(output.next_node_id),
    });
  }
  if (output.ok === true || output.ok === 'true') {
    rows.push({ label: 'Ação', value: 'Executada sem erro aparente.' });
  }
  if (output.status_code != null && output.status_code !== '') {
    rows.push({
      label: 'Código HTTP da resposta',
      value: String(output.status_code),
    });
  }
  if (output.body != null && String(output.body).trim() !== '') {
    const b = String(output.body);
    rows.push({
      label: 'Corpo da resposta (trecho)',
      value: b.length > 220 ? `${b.slice(0, 220)}…` : b,
    });
  }
  if (output.error != null && output.error !== '') {
    rows.push({ label: 'Erro retornado', value: String(output.error) });
  }
  if (output.message != null && output.message !== '') {
    rows.push({ label: 'Mensagem do sistema', value: String(output.message) });
  }
  if (output.performed === true) {
    rows.push({ label: 'Resultado', value: 'Concluído.' });
  }

  const known = new Set([
    'branch_handle',
    'advanced',
    'scheduled',
    'wait_minutes',
    'next_node_id',
    'ok',
    'status_code',
    'body',
    'error',
    'message',
    'performed',
    'headers_json',
  ]);
  Object.entries(output).forEach(([k, v]) => {
    if (known.has(k)) return;
    rows.push({ label: humanizeKey(k), value: formatScalar(v) });
  });

  return rows;
};

const detailBlocks = computed(() => {
  const step = selectedStep.value;
  if (!step) return [];

  const input = step.input && typeof step.input === 'object' ? step.input : {};
  const bindings = input.bindings;
  const config = input.config;
  const stepKind =
    typeof step.kind === 'string' && step.kind !== '' ? step.kind : config?.kind;

  const blocks = [
    {
      id: 'context',
      title: 'O que o fluxo “enxergava” neste momento',
      hint: 'Resumo da conversa, do cliente e de outros dados usados automaticamente neste passo.',
      rows: friendlyBindingRows(bindings),
    },
    {
      id: 'settings',
      title: 'O que você configurou neste bloco',
      hint: 'Valores definidos no editor do fluxo (textos, opções, URLs, etc.).',
      rows: friendlyConfigRows(config, { kind: stepKind, bindings }),
    },
    {
      id: 'result',
      title: 'O que aconteceu em seguida',
      hint: 'Resultado deste passo: resposta de API, caminho escolhido na condição, confirmação de envio, etc.',
      rows: friendlyOutputRows(
        step.output && typeof step.output === 'object' ? step.output : {}
      ),
    },
  ];

  return blocks;
});

const formatTs = ts => {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n)) return '—';
  return new Date(n * 1000).toLocaleString('pt-BR');
};

const stepStatusClass = status => {
  const map = {
    success: 'border-n-teal-7 bg-n-teal-3/40',
    waiting: 'border-n-amber-7 bg-n-amber-3/40',
    error: 'border-n-ruby-7 bg-n-ruby-3/40',
  };
  return map[status] || 'border-n-slate-6 bg-n-alpha-2';
};

const stepDotClass = status => {
  const map = {
    success: 'bg-n-teal-9',
    waiting: 'bg-n-amber-9',
    error: 'bg-n-ruby-9',
  };
  return map[status] || 'bg-n-slate-9';
};

const nodeTitle = step => {
  if (!step) return '—';
  const id = step.node_id;
  if (id === '__resume__') return 'Retomada (após espera)';
  const n = nodesById.value[id];
  if (n?.data) {
    const d = n.data;
    const label = d.label || d.title;
    if (label) return String(label);
  }
  return step.kind || '—';
};

const formatJson = v => {
  if (v == null || v === '') return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const load = async () => {
  if (!workflowId.value || !executionId.value) return;
  isLoading.value = true;
  try {
    const [exRes, wfRes] = await Promise.all([
      conversationWorkflowsAPI.executionShow(
        workflowId.value,
        executionId.value
      ),
      conversationWorkflowsAPI.show(workflowId.value),
    ]);
    payload.value = exRes.data?.payload ?? {};
    const g = wfRes.data?.payload?.graph;
    graph.value =
      g && typeof g === 'object'
        ? { nodes: g.nodes || [], edges: g.edges || [] }
        : { nodes: [], edges: [] };
    selectedIndex.value = 0;
  } catch {
    useAlert('Não foi possível carregar a execução.');
    payload.value = {};
    graph.value = { nodes: [], edges: [] };
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

watch(executionId, () => {
  load();
});

const goToList = () => {
  router.push({
    name: 'conversation_workflows_executions',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
    },
  });
};

const selectStep = idx => {
  selectedIndex.value = idx;
  showTechnical.value = false;
};

const canCopyToEditor = computed(() => {
  if (!trace.value.length) return false;
  return trace.value.some(s => {
    const b = s?.input?.bindings;
    return (
      b &&
      typeof b === 'object' &&
      !Array.isArray(b) &&
      Object.keys(b).length > 0
    );
  });
});

const copyToEditor = () => {
  const bindings = lastBindingsFromExecutionTrace(trace.value);
  if (!Object.keys(bindings).length) {
    useAlert('Não há dados de contexto nesta execução para copiar.');
    return;
  }
  writePinnedExecution(accountId.value, workflowId.value, {
    executionId: payload.value.id,
    copiedAt: new Date().toISOString(),
    bindings,
  });
  useAlert(
    'Dados fixados. Abrindo o editor — veja os valores no painel «Dados entre nós» ao configurar um nó.'
  );
  router.push({
    name: 'conversation_workflows_edit',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
    },
  });
};
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex flex-col bg-n-surface-1 text-n-slate-12 border-t border-n-weak"
  >
    <header
      class="flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2 sm:px-4"
    >
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        title="Voltar ao histórico"
        @click="goToList"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-5 shrink-0"
          aria-hidden="true"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <div class="min-w-0 flex-1">
        <h1 class="truncate text-base font-medium text-n-slate-12">
          Execução #{{ payload.id ?? '—' }}
        </h1>
        <p class="truncate text-xs text-n-slate-11">
          {{ payload.trigger_kind }} · conversa
          {{ payload.conversation_id }} · {{ formatTs(payload.started_at) }}
        </p>
      </div>
      <Button
        v-if="trace.length > 0"
        slate
        outline
        size="sm"
        label="Copiar para o editor"
        :disabled="!canCopyToEditor || isLoading"
        @click="copyToEditor"
      />
      <Button
        slate
        outline
        size="sm"
        label="Atualizar"
        :is-loading="isLoading"
        @click="load"
      />
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      Carregando…
    </div>

    <div
      v-else-if="trace.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-n-slate-11"
    >
      <p>Nenhum passo registrado para esta execução.</p>
      <p class="max-w-md text-xs">
        Execuções antigas ou criadas antes da telemetria por nó não incluem o
        fluxo de dados. Dispare o fluxo novamente para ver os detalhes.
      </p>
    </div>

    <div
      v-else
      class="flex min-h-0 flex-1 flex-col lg:flex-row"
    >
      <aside
        class="flex max-h-[40vh] w-full shrink-0 flex-col overflow-hidden border-b border-n-weak bg-n-solid-1 lg:max-h-none lg:w-[min(22rem,100%)] lg:border-b-0 lg:border-r"
      >
        <div class="shrink-0 border-b border-n-weak px-3 py-2">
          <div class="text-xs font-medium text-n-slate-12">
            Etapas ({{ trace.length }})
          </div>
          <div class="mt-0.5 text-[11px] leading-snug text-n-slate-10">
            Toque em uma etapa para ver o que aconteceu em linguagem simples.
          </div>
        </div>
        <ul class="min-h-0 flex-1 overflow-y-auto p-2">
          <li v-for="(step, idx) in trace" :key="idx">
            <button
              type="button"
              class="mb-1 flex w-full rounded-lg border-l-4 px-2 py-2 text-left transition-colors hover:bg-n-alpha-2"
              :class="[
                stepStatusClass(step.status),
                selectedIndex === idx ? 'ring-2 ring-n-teal-8' : '',
              ]"
              @click="selectStep(idx)"
            >
              <span
                class="mt-1 mr-2 size-2 shrink-0 rounded-full"
                :class="stepDotClass(step.status)"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                  :title="nodeTitle(step)"
                >
                  {{ nodeTitle(step) }}
                </span>
                <span class="block truncate font-mono text-xs text-n-slate-11">
                  {{ step.node_type }} · {{ step.kind }}
                </span>
                <span class="block text-xs text-n-slate-10">
                  {{ formatTs(step.at) }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </aside>

      <section
        class="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-3 sm:p-4"
      >
        <div
          v-if="selectedStep?.error_message"
          class="rounded-lg border border-n-ruby-7 bg-n-ruby-3/30 px-3 py-2 text-sm text-n-ruby-12"
        >
          <span class="font-medium">Algo deu errado nesta etapa:</span>
          {{ selectedStep.error_message }}
        </div>

        <div class="flex flex-col gap-4">
          <article
            v-for="block in detailBlocks"
            :key="block.id"
            class="rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <header class="border-b border-n-weak px-4 py-3">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ block.title }}
              </h2>
              <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
                {{ block.hint }}
              </p>
            </header>
            <div v-if="block.rows.length" class="divide-y divide-n-weak px-4">
              <div
                v-for="(row, ri) in block.rows"
                :key="`${block.id}-${ri}`"
                class="grid grid-cols-1 gap-1 py-3 sm:grid-cols-12 sm:gap-3"
              >
                <div class="text-sm font-medium text-n-slate-11 sm:col-span-4">
                  {{ row.label }}
                </div>
                <div
                  class="whitespace-pre-wrap break-words text-sm text-n-slate-12 sm:col-span-8"
                >
                  {{ row.value }}
                </div>
              </div>
            </div>
            <p
              v-else
              class="px-4 py-6 text-center text-sm text-n-slate-10"
            >
              Nada foi registrado nesta parte para esta etapa.
            </p>
          </article>
        </div>

        <div class="rounded-xl border border-n-weak/80 bg-n-alpha-2">
          <button
            type="button"
            class="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            @click="showTechnical = !showTechnical"
          >
            <span>Detalhes técnicos (JSON)</span>
            <span class="text-n-slate-11">{{ showTechnical ? '▲' : '▼' }}</span>
          </button>
          <div
            v-if="showTechnical"
            class="space-y-3 border-t border-n-weak px-4 pb-4 pt-3"
          >
            <div>
              <div class="mb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
                Entrada bruta
              </div>
              <pre
                class="max-h-48 overflow-auto rounded-lg bg-n-slate-2 p-3 font-mono text-[11px] leading-relaxed text-n-slate-12"
              >{{ formatJson(selectedStep?.input) }}</pre>
            </div>
            <div>
              <div class="mb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
                Saída bruta
              </div>
              <pre
                class="max-h-48 overflow-auto rounded-lg bg-n-slate-2 p-3 font-mono text-[11px] leading-relaxed text-n-slate-12"
              >{{ formatJson(selectedStep?.output) }}</pre>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
